import { API_BASE } from '../config';
import {
  trackProjectImportAnalysisStarted,
  trackProjectImportAnalysisSuccess,
  trackProjectImportAnalysisFailed,
} from './analytics';

export type ImportAnalysisTriggerSource = 'feedback' | 'characters' | 'locations';

export type ImportAnalysisCreditEstimates = Record<ImportAnalysisTriggerSource, number>;

export interface ImportAnalysisCreditEstimatesInput {
  suggestionsText: string;
  manuscriptText: string;
}

export async function fetchImportAnalysisCreditEstimates(
  token: string,
  input: ImportAnalysisCreditEstimatesInput
): Promise<ImportAnalysisCreditEstimates> {
  const response = await fetch(`${API_BASE}/api/import-analysis/credit-estimates`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch import analysis credit estimates');
  }

  const data = (await response.json()) as { estimates: ImportAnalysisCreditEstimates };
  return data.estimates;
}

export type ImportAnalysisPanel = 'suggestions' | 'characters' | 'locations';

export interface ImportAnalysisSelection {
  suggestions: boolean;
  characters: boolean;
  locations: boolean;
}

export interface RunImportAnalysesParams {
  documentId: string;
  projectId: string | null;
  suggestionsText: string;
  selection: ImportAnalysisSelection;
}

const POLL_INTERVAL_MS = 2000;
const MAX_ANALYSIS_DURATION_MS = 180000;
const REQUEST_TIMEOUT_MS = 120000;

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

async function runSuggestionsAnalysis(token: string, documentId: string, text: string): Promise<boolean> {
  if (!text.trim()) {
    return false;
  }
  const response = await fetchWithTimeout(
    `${API_BASE}/api/feedback`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(token),
      },
      body: JSON.stringify({ text, documentId, forceNew: true }),
    },
    REQUEST_TIMEOUT_MS
  );

  if (!response.ok || !response.body) {
    return false;
  }

  const reader = response.body.getReader();
  while (true) {
    const { done } = await reader.read();
    if (done) {
      break;
    }
  }
  return true;
}

async function runCharacterAnalysis(token: string, documentId: string): Promise<void> {
  const deadline = Date.now() + MAX_ANALYSIS_DURATION_MS;
  while (Date.now() < deadline) {
    const response = await fetchWithTimeout(
      `${API_BASE}/api/documents/${documentId}/characters/analyze`,
      { method: 'POST', headers: authHeaders(token) },
      REQUEST_TIMEOUT_MS
    );

    if (response.status === 202) {
      const data = await response.json().catch(() => ({}));
      if (data.status === 'processing') {
        await sleep(POLL_INTERVAL_MS);
        continue;
      }
    }
    return;
  }
}

async function runLocationAnalysis(token: string, documentId: string): Promise<void> {
  const triggerResponse = await fetchWithTimeout(
    `${API_BASE}/api/documents/${documentId}/locations?regenerate=true`,
    { headers: authHeaders(token) },
    REQUEST_TIMEOUT_MS
  );

  if (!triggerResponse.ok && triggerResponse.status !== 202) {
    return;
  }
  if (triggerResponse.status !== 202) {
    return;
  }

  const triggerData = await triggerResponse.json().catch(() => ({}));
  if (triggerData.status !== 'processing') {
    return;
  }

  const deadline = Date.now() + MAX_ANALYSIS_DURATION_MS;
  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS);
    const response = await fetchWithTimeout(
      `${API_BASE}/api/documents/${documentId}/locations`,
      { headers: authHeaders(token) },
      REQUEST_TIMEOUT_MS
    );
    if (!response.ok) {
      continue;
    }
    const data = await response.json().catch(() => ({}));
    const count = Array.isArray(data.locations)
      ? data.locations.length
      : Array.isArray(data)
        ? data.length
        : 0;
    if (count > 0) {
      return;
    }
  }
}

export function firstRequestedAnalysisPanel(
  selection: ImportAnalysisSelection
): ImportAnalysisPanel | null {
  if (selection.suggestions) {
    return 'suggestions';
  }
  if (selection.characters) {
    return 'characters';
  }
  if (selection.locations) {
    return 'locations';
  }
  return null;
}

async function fetchHasSuggestions(token: string, documentId: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/api/suggestions/documents/${documentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return false;
    const data = await response.json();
    return Array.isArray(data.suggestions) && data.suggestions.length > 0;
  } catch {
    return false;
  }
}

export interface RunImportAnalysesResult {
  hasSuggestions: boolean;
}

export async function runImportAnalyses(
  token: string,
  { documentId, suggestionsText, selection }: RunImportAnalysesParams
): Promise<RunImportAnalysesResult> {
  const tasks: Array<Promise<boolean | void>> = [];
  if (selection.suggestions) {
    tasks.push(runSuggestionsAnalysis(token, documentId, suggestionsText));
  }
  if (selection.characters) {
    tasks.push(runCharacterAnalysis(token, documentId));
  }
  if (selection.locations) {
    tasks.push(runLocationAnalysis(token, documentId));
  }

  if (tasks.length === 0) {
    return { hasSuggestions: false };
  }

  const options = {
    suggestions: selection.suggestions,
    characters: selection.characters,
    places: selection.locations,
  };

  trackProjectImportAnalysisStarted(options);
  const results = await Promise.allSettled(tasks);
  if (results.some((result) => result.status === 'rejected')) {
    trackProjectImportAnalysisFailed(options);
  } else {
    trackProjectImportAnalysisSuccess(options);
  }

  const hasSuggestions = selection.suggestions
    ? await fetchHasSuggestions(token, documentId)
    : false;

  return { hasSuggestions };
}
